import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import Select from 'react-select-5';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from 'modules/routes';
import AddQuestionImage from './Add';
import Card from './Card';
import { QuestionOrQuestionGroupType, ImageModelOpts, useImageModels, ImageModelResponseType } from './utils';
import TablePromptAnswerKeyButton from './TablePromptAnswerKeyButton';

interface ManagerProps {
  forAnswerKey?: boolean;
  modelId: number;
  questionType: QuestionOrQuestionGroupType;
  userType: UserType;
  withTableAnswerKey?: boolean;
  automaticTranslation?: boolean;
  stimulusPos?: string;
  exerciseType?: string;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});

type SelectData = { label: string, value: string };

const Manager = ({
  forAnswerKey, automaticTranslation, modelId, questionType, userType, exerciseType, withTableAnswerKey, stimulusPos,
}: ManagerProps) => {
  const STIMULUS_POSITION_OPTIONS = [
    { label: 'Below Prompt', value: 'below' },
    { label: 'Above Prompt', value: 'above' },
  ];

  const [stimulusPositionSelect, setStimulusPositionSelect] = useState<SelectData>(STIMULUS_POSITION_OPTIONS[0]);
  const [hasVideoStimulus, setVideoStimulus] = useState(false);

  const canAddVideo = ['Sysadmin', 'Admin', 'ContentManager'].includes(userType);

  const nonBackgroundImages = questionType === 'DragAndDropQuestion';
  const modelType = questionType === 'QuestionGroup' ? 'QuestionGroup' : 'Question';
  const forGridQuestion = questionType === 'GridQuestion';
  const stimulusPositionEnabled = !(forAnswerKey || ['ReadingGame', 'Assessment'].includes(exerciseType));

  const opts: ImageModelOpts = { forAnswerKey, nonBackgroundImages, modelType, forGridQuestion };
  const { data: imageModels, refetch } = useImageModels(modelId, opts);

  const stimulusPositionUpdate = (newPos : SelectData) => {
    setStimulusPositionSelect(newPos);

    if (!stimulusPositionEnabled) return;

    axios.patch(Routes.plato_api_question_path(modelId), {
      data: { stimulus_position: newPos.value },
    }).catch(err => console.error(err));
  };

  useEffect(() => {
    window.addEventListener('imageAdded', () => refetch());

    if (stimulusPos) {
      setStimulusPositionSelect(STIMULUS_POSITION_OPTIONS.find(position => position.value === stimulusPos));
    }

    return () => {
      window.removeEventListener('imageAdded', () => refetch());
    };
  }, []);

  useEffect(() => {
    const form = document.getElementById(`question-${modelId}-body`);
    const config = { childList: true, subtree: true };

    const callback = (mutationsList: MutationRecord[]) => {
      const videoStimulus = document.getElementById(`question-${modelId}-body`).querySelector('video') || false;

      mutationsList.forEach((mutation) => {
        if (mutation.addedNodes.length > 0 && mutation.addedNodes[0].nodeName === 'VIDEO') {
          setVideoStimulus(!!videoStimulus);
          if (!stimulusPos) stimulusPositionUpdate(STIMULUS_POSITION_OPTIONS[0]);
          return;
        }
        if (mutation.target?.nodeName === 'VIDEO' && mutation.removedNodes.length > 0) {
          setVideoStimulus(!!videoStimulus);
        }
      });
    };

    const observer = new MutationObserver(callback);
    if (form) observer.observe(form, config);

    return () => {
      if (form) observer.disconnect();
    };
  }, [modelId]);

  const afterSubmit = () => {
    if (!stimulusPos) stimulusPositionUpdate(STIMULUS_POSITION_OPTIONS[0]);

    refetch();
  };

  const afterEdit = (imageModel: ImageModelResponseType[] | any) => {
    if (!(imageModel?.length || imageModel) || questionType !== 'GridQuestion') {
      refetch();
      return;
    }

    const imageModelToSave = imageModel[0] || imageModel;
    const imageElement = document.querySelector(`img[data-snippet="[IMAGE_MODEL_${imageModelToSave.id}]"`);

    if (!imageElement) {
      refetch();
      return;
    }

    const newUrl = imageModelToSave.image.imageUrl.replace('/original/', `/${imageModelToSave.displaySize}/`);
    const originalUrl = imageModelToSave.image.imageUrl;
    const $targetDiv = $(imageElement).parents('.grid_td_wrapper_div').children('.grid_td_content_div')[0];
    const formId = $(imageElement).parents('form').attr('id');

    imageElement.setAttribute('src', newUrl);
    imageElement.setAttribute('data-zoomurl', originalUrl);
    $targetDiv.dispatchEvent(new CustomEvent(`${formId}-SYNC`));

    refetch();
  };

  const afterDelete = (imageModelId) => {
    if (!imageModelId || questionType !== 'GridQuestion') {
      refetch();
      return;
    }

    const imgSelector = `img[data-snippet="[IMAGE_MODEL_${imageModelId}]"`;
    const imageElement = document.querySelector(imgSelector);

    if (!imageElement) {
      refetch();
      return;
    }

    const targetDiv = $(imageElement).parents('.grid_td_wrapper_div').children('.grid_td_content_div')[0];
    const formId = $(imageElement).parents('form').attr('id');

    imageElement.remove();
    targetDiv.dispatchEvent(new CustomEvent(`${formId}-SYNC`));

    refetch();
  };

  const mediaType = () => {
    if (forAnswerKey) return 'answer';
    if (questionType === 'DrawingQuestion') return 'background';

    return 'question';
  };

  const hasStimulusImage = () => {
    if (imageModels?.length < 1) return false;
    if (questionType === 'DrawingQuestion') return imageModels.some(model => model.media_meta.type !== 'background');
    if (questionType === 'GridQuestion') return imageModels.some(model => model.media_meta.type !== 'in_grid');

    return true;
  };

  const hasImageModels = (imageModels || []).length > 0;
  const canAddImage = () => {
    if (questionType === 'DrawingQuestion') return imageModels?.length < 2;
    if (questionType === 'GridQuestion') {
      const nonGrid = (imageModels.find(model => model.media_meta.type !== 'in_grid')
        || []).length < 1;
      return !hasImageModels || nonGrid;
    }

    return !hasImageModels;
  };

  const tablePromptImageModel = (imageModels || [])[0];

  return (
    <>
      {forAnswerKey && (
        <div className="mb5">
          <p className="tw-my-4 tw-font-bold tw-inline-block">Image</p>
        </div>
      )}
      <div className={clsx('tw-flex tw-gap-1', { 'tw-mb-4 -tw-mt-3': forAnswerKey })}>
        <>
          {canAddImage() && (
            <AddQuestionImage
              afterSubmit={afterSubmit}
              buttonText={forAnswerKey ? 'Add Answer Image' : 'Add Image'}
              forAnswerKey={forAnswerKey}
              mediaType={mediaType()}
              modelId={modelId}
              modelType={modelType}
              userType={userType}
            />
          )}
          {withTableAnswerKey && (
            <TablePromptAnswerKeyButton
              afterSubmit={afterSubmit}
              automaticTranslation={automaticTranslation}
              imageModel={tablePromptImageModel}
              questionId={modelId}
            />
          )}
        </>
        {!forAnswerKey && (
          <AddQuestionImage
            afterSubmit={afterSubmit}
            buttonText="Add GIF"
            mediaType={mediaType()}
            modelId={modelId}
            modelType={modelType}
            userType={userType}
          />
        )}
        {canAddVideo && <span className="input" id={`add-video-button-question-${modelId}`} />}
      </div>
      {stimulusPositionEnabled && (hasVideoStimulus || hasStimulusImage()) && (
        <div className="tw-flex tw-mt-8 tw-items-center">
          <p className="tw-font-bold tw-mr-6"> Stimulus Location </p>
          <Select
            className="tw-w-[30%]"
            options={STIMULUS_POSITION_OPTIONS}
            value={stimulusPositionSelect}
            onChange={stimulusPositionUpdate}
          />
        </div>
      )}
      {hasImageModels && !forAnswerKey && (
        <p className="tw-my-4 tw-font-bold tw-inline-block">Image</p>
      )}
      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-justify-between tw-gap-4">
        {(imageModels || []).map(imageModel => (
          <Card
            key={imageModel.id}
            afterDelete={afterDelete}
            afterSubmit={afterEdit}
            forAnswerKey={forAnswerKey}
            imageModel={imageModel}
            mediaType={mediaType()}
            modelId={modelId}
            modelType={modelType}
            questionType={questionType}
            userType={userType}
          />
        ))}
      </div>
    </>
  );
};

const ManagerWrapper = (props: ManagerProps) => (
  <QueryClientProvider client={queryClient}>
    <Manager {...props} />
  </QueryClientProvider>
);

export default ManagerWrapper;
